<script lang="ts">
  import { onMount } from 'svelte';
  import { isReportQueryWithinLimit, REPORT_QUERY_TOO_LARGE_MESSAGE } from './utils/report-query';
  import * as yup from 'yup';
  import { isEqual } from 'lodash';

  import {
    DatePicker,
    DatePickerInput,
    MultiSelect,
    Button,
    DataTable,
    Toggle,
    InlineNotification,
    OverflowMenu,
    OverflowMenuItem,
  } from 'carbon-components-svelte';
  import Download16 from 'carbon-icons-svelte/lib/Download16';
  import FilterReset16 from 'carbon-icons-svelte/lib/FilterReset16';
  import type { DataTableRow } from 'carbon-components-svelte/src/DataTable/DataTable.svelte';

  import { PERMISSION_AVAILABILITY_MAP, PERMISSION_TYPE_LABEL_MAP } from '../../utils/permission-types';
  import {
    getDataFeedInventorySummary,
    getEntitlementsReport,
    getEntitlementsReportCsvUrl,
    getEntitlementsReportSearchParams,
    getOrganizations,
    type DataFeedInventorySummaryRow,
    type EntitlementsPermissionType,
    type EntitlementsReportRow,
    type EntitlementsReportRequest,
    type Organization,
    type PaginationParams,
  } from '../../services';
  import LoadingSpinner from '../../components/LoadingSpinner.svelte';
  import PaginationWithRouting from '../../components/PaginationWithRouting.svelte';
  import StatusIcon from '../../components/StatusIcon.svelte';
  import { noopSort, asOptionalTextColumn, parseSortEvent } from '../../utils';
  import { ENTITLEMENTS_REPORT_FILTER_QUERY_PARAMS, ENTITLEMENTS_REPORT_QUERY_PARAMS } from './utils/entitlements-report-routing';

  const DEFAULT_ENTITLEMENT_TYPES: EntitlementsPermissionType[] = [
    'data-feeds-lake',
    'data-feeds-aws-arbitrated-pcaps',
    'data-feeds-aws-raw',
  ];

  let pageData: { loading: boolean; stale?: boolean; error?: string } = { loading: true };
  let dataFeeds: DataFeedInventorySummaryRow[] = [];
  let filteredDataFeeds: DataFeedInventorySummaryRow[] = dataFeeds;
  let organizations: Organization[] = [];

  let entitlements: EntitlementsReportRow[] = [];
  let entitlementsCount = 0;

  const form: EntitlementsReportRequest = {} as EntitlementsReportRequest;

  const pagination: PaginationParams = {
    orderBy: null,
    orderDirection: null,
    currentPage: 1,
    pageSize: 10,
  };

  const setFormFromUrlParams = () => {
    const url = new URL(window.location.href);
    form.organizations = url.searchParams.getAll(ENTITLEMENTS_REPORT_QUERY_PARAMS.organizations);
    const urlTypes = url.searchParams.getAll(ENTITLEMENTS_REPORT_QUERY_PARAMS.types) as EntitlementsPermissionType[];
    form.types = urlTypes.length ? urlTypes : DEFAULT_ENTITLEMENT_TYPES;
    form.feedCodes = url.searchParams.getAll(ENTITLEMENTS_REPORT_QUERY_PARAMS.feedCodes);
    form.startDate = url.searchParams.get(ENTITLEMENTS_REPORT_QUERY_PARAMS.startDate) || '';
    form.ongoing = url.searchParams.get(ENTITLEMENTS_REPORT_QUERY_PARAMS.ongoing) === 'true';
  };

  const hasReportFiltersInUrl = () => {
    const searchParams = new URL(window.location.href).searchParams;
    return ENTITLEMENTS_REPORT_FILTER_QUERY_PARAMS.some((param) => searchParams.has(param));
  };

  setFormFromUrlParams();

  const errors = {
    organizations: '',
    types: '',
    feedCodes: '',
    startDate: '',
  };

  const yupDate = yup
    .date()
    .nullable()
    .transform((curr, orig) => (orig === '' ? null : curr));

  const schema = yup.object().shape({
    organizations: yup.array().of(yup.string()),
    types: yup.array().of(yup.string()).min(1, 'At least one entitlement type must be selected'),
    feedCodes: yup.array().of(yup.string()),
    startDate: yupDate.typeError('A valid date is required').max(new Date(), 'Start date cannot be in the future'),
    ongoing: yup.boolean(),
  });

  onMount(async () => {
    try {
      [dataFeeds, organizations] = await Promise.all([getDataFeedInventorySummary(), getOrganizations()]);
      filteredDataFeeds = dataFeeds.filter((feed) => form.types.some((type) => feed[PERMISSION_AVAILABILITY_MAP[type]] === true));

      if (hasReportFiltersInUrl()) {
        await generateReport({ resetPagination: false, updateUrl: false });
      } else {
        pageData = { loading: false };
      }
    } catch (error) {
      console.error('[EntitlementsReport] Failed to load data feeds or organizations!', error);
      pageData = { loading: false, error: 'Failed to load data feeds or organizations!' };
    }
  });

  const onPopstateChange = () => {
    setFormFromUrlParams();
    pageData.stale = true;
  };

  const onMultiSelectChange = (prop: keyof EntitlementsReportRequest, callback: (event: CustomEvent) => void) => (event: CustomEvent) => {
    // MultiSelect fires an event on initialization. Avoid processing it if nothing changed
    if (!isEqual(form[prop], event.detail.selectedIds)) callback(event);
  };

  const onOrganizationChange = onMultiSelectChange('organizations', (event) => {
    form.organizations = event.detail.selectedIds as string[];
    errors.organizations = '';
  });

  const onTypeChange = onMultiSelectChange('types', (event) => {
    form.types = event.detail.selectedIds as EntitlementsReportRequest['types'];

    filteredDataFeeds = dataFeeds.filter((feed) => form.types.some((type) => feed[PERMISSION_AVAILABILITY_MAP[type]] === true));

    form.feedCodes = form.feedCodes.filter((code) => filteredDataFeeds.some((feed) => feed.id === code));
    errors.types = '';
  });

  const onFeedCodeChange = onMultiSelectChange('feedCodes', (event) => {
    form.feedCodes = event.detail.selectedIds as string[];
    errors.feedCodes = '';
  });

  const onStartDateChange = (event: CustomEvent) => {
    form.startDate = event.detail.dateStr;
    errors.startDate = '';
  };

  const onToggleOngoing = (event: CustomEvent) => {
    form.ongoing = event.detail.toggled;
  };

  const resetFilters = () => {
    form.organizations = [];
    form.types = DEFAULT_ENTITLEMENT_TYPES;
    form.feedCodes = [];
    form.startDate = '';
    form.ongoing = false;

    filteredDataFeeds = dataFeeds;

    errors.organizations = '';
    errors.types = '';
    errors.feedCodes = '';
    errors.startDate = '';

    pageData.stale = false;
  };

  const validateForm = async () => {
    try {
      await schema.validate(form, { abortEarly: false });
      return true;
    } catch (error) {
      (error as yup.ValidationError).inner.forEach((e) => (errors[e.path as keyof typeof errors] = e.message));
      return false;
    }
  };

  const validateQuerySize = (...queries: URLSearchParams[]) => {
    if (queries.every(isReportQueryWithinLimit)) {
      if (pageData.error === REPORT_QUERY_TOO_LARGE_MESSAGE) {
        const { error, ...nextPageData } = pageData;
        pageData = nextPageData;
      }
      return true;
    }
    pageData = { loading: false, stale: true, error: REPORT_QUERY_TOO_LARGE_MESSAGE };
    return false;
  };

  const onDownloadReportClick = async () => {
    if (!(await validateForm())) return;

    const csvUrl = getEntitlementsReportCsvUrl(form);
    if (!validateQuerySize(new URL(csvUrl).searchParams)) return;

    window.location.href = csvUrl;
  };

  const onGenerateReportClick = async () => {
    void generateReport({ resetPagination: true });
  };

  const onSortChange = (event: CustomEvent) =>
    parseSortEvent(event, ({ orderBy, orderDirection }) => {
      pagination.orderBy = orderBy;
      pagination.orderDirection = orderDirection;
      void generateReport({ resetPagination: true });
    });

  const onPaginationChange = () => {
    void generateReport({ resetPagination: false });
  };

  const openInNewWindow = (path: string) => {
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const goToOrganization = (organizationId: string) => () => {
    openInNewWindow(`/organization/${organizationId}`);
  };

  const goToPermission = (groupId: string, permissionId: string, permissionType: string) => () => {
    openInNewWindow(`/group/${groupId}?permissionId=${permissionId}#permissions-${permissionType}`);
  };

  const generateReport = async ({ resetPagination, updateUrl = true }: { resetPagination: boolean; updateUrl?: boolean }) => {
    if (!(await validateForm())) return;

    if (resetPagination) {
      pagination.currentPage = 1;
    }

    const requestQuery = getEntitlementsReportSearchParams(form, pagination);
    const reportUrl = getUrlFromForm();
    if (!validateQuerySize(requestQuery, reportUrl.searchParams)) {
      return;
    }

    pageData = { loading: true };
    try {
      if (updateUrl) {
        window.history.pushState({}, '', reportUrl);
      }

      const { items, count } = await getEntitlementsReport(form, pagination);
      entitlements = items;
      entitlementsCount = count;

      pageData = { loading: false };
    } catch (error) {
      console.error('[EntitlementsReport] Failed to get entitlements report!', error);
      pageData = { loading: false, stale: true, error: 'Failed to get entitlements report!' };
    }
  };

  const getUrlFromForm = () => {
    const url = new URL(window.location.href);
    url.searchParams.set('page', pagination.currentPage.toString());
    url.searchParams.set('pageSize', pagination.pageSize.toString());

    url.searchParams.delete(ENTITLEMENTS_REPORT_QUERY_PARAMS.organizations);
    form.organizations.forEach((org) => url.searchParams.append(ENTITLEMENTS_REPORT_QUERY_PARAMS.organizations, org));

    url.searchParams.delete(ENTITLEMENTS_REPORT_QUERY_PARAMS.types);
    form.types.forEach((type) => url.searchParams.append(ENTITLEMENTS_REPORT_QUERY_PARAMS.types, type));

    url.searchParams.delete(ENTITLEMENTS_REPORT_QUERY_PARAMS.feedCodes);
    form.feedCodes.forEach((feed) => url.searchParams.append(ENTITLEMENTS_REPORT_QUERY_PARAMS.feedCodes, feed));

    url.searchParams.set(ENTITLEMENTS_REPORT_QUERY_PARAMS.startDate, form.startDate);
    url.searchParams.set(ENTITLEMENTS_REPORT_QUERY_PARAMS.ongoing, form.ongoing ? 'true' : 'false');

    return url;
  };

  const getPermissionType = (row: DataTableRow): string => {
    const entitlement = row as EntitlementsReportRow;
    return PERMISSION_TYPE_LABEL_MAP[entitlement.permission.type as keyof typeof PERMISSION_TYPE_LABEL_MAP] || entitlement.permission.type;
  };
</script>

{#if pageData.error}
  <InlineNotification hideCloseButton kind="error" lowContrast title="Error:" subtitle={pageData.error} />
{/if}

<LoadingSpinner loading={pageData.loading}>
  <div class="filters-reset-button">
    <Button on:click={resetFilters} iconDescription="Reset filters" icon={FilterReset16} kind="ghost">Reset filters</Button>
  </div>

  <div class="filters-row">
    <MultiSelect
      spellcheck="false"
      filterable
      labelText="Organization"
      placeholder="Select organizations..."
      selectedIds={form.organizations}
      on:select={onOrganizationChange}
      items={organizations.map(({ id, name }) => ({ id, text: name }))}
      invalidText={errors.organizations}
      invalid={!!errors.organizations}
    />

    <MultiSelect
      labelText="Entitlement Type"
      label="Select entitlement types..."
      on:select={onTypeChange}
      selectedIds={form.types}
      items={DEFAULT_ENTITLEMENT_TYPES.map((type) => ({ id: type, text: PERMISSION_TYPE_LABEL_MAP[type] || type }))}
      sortItem={noopSort}
      invalidText={errors.types}
      invalid={!!errors.types}
    />
  </div>

  <div class="filters-row filters-row-margin">
    <MultiSelect
      spellcheck="false"
      filterable
      labelText="Data feed"
      placeholder="Select data feed..."
      selectedIds={form.feedCodes}
      on:select={onFeedCodeChange}
      items={filteredDataFeeds.map(({ id, name }) => ({ id, text: name }))}
      invalidText={errors.feedCodes}
      invalid={!!errors.feedCodes}
    />

    <div class="date-range-picker">
      {#key form.ongoing}
        <DatePicker
          datePickerType="single"
          flatpickrProps={{ static: true }}
          on:change={onStartDateChange}
          value={form.startDate}
          dateFormat="Y-m-d"
        >
          <DatePickerInput
            labelText="Start date"
            placeholder="yyyy-mm-dd"
            name="from"
            autocomplete="off"
            invalidText={errors.startDate}
            invalid={!!errors.startDate}
          />
        </DatePicker>
      {/key}
      <Toggle labelText="Ongoing" toggled={form.ongoing} on:toggle={onToggleOngoing} labelA="" labelB="" />
    </div>

    <div class="generate-report-buttons">
      <Button on:click={onDownloadReportClick} iconDescription="Download CSV report" icon={Download16} kind="tertiary" />
      <Button on:click={onGenerateReportClick}>Generate report</Button>
    </div>
  </div>

  {#if pageData.stale}
    <div class="stale-data-notification">
      <strong>Filters changed:</strong> These results reflect a previous query. Re-run the report to see current data.
    </div>
  {/if}

  <DataTable
    headers={[
      { key: 'feed.name', value: 'Feed Code', sort: noopSort },
      { key: 'permission.group.name', value: 'Group', sort: noopSort },
      { key: 'permission.group.organization.name', value: 'Organization', sort: noopSort },
      { key: 'startDate', value: 'Start Date', sort: noopSort },
      { key: 'endDate', value: 'End Date', sort: noopSort },
      { key: 'isOngoing', value: 'Ongoing', sort: noopSort },
      { key: 'isDayEntitlement', value: 'Day Entitlement', sort: noopSort },
      { key: 'expiryDate', value: 'Expiry date', ...asOptionalTextColumn, sort: noopSort },
      { key: 'permission.type', value: 'Location', sort: noopSort },
      { key: 'actions', empty: true, sort: false },
    ]}
    rows={entitlements}
    sortable
    on:click:header={onSortChange}
  >
    <svelte:fragment slot="cell" let:cell let:row>
      {#if cell.key === 'isOngoing'}
        <div class="ongoing-cell">
          <StatusIcon valid={cell.value} />
          {#if row.onGoingMaxDate}
            <em>until {row.onGoingMaxDate}</em>
          {/if}
        </div>
      {:else if cell.key === 'isDayEntitlement'}
        <StatusIcon valid={cell.value} />
      {:else if cell.key === 'actions'}
        <OverflowMenu aria-label="Open and close list of options" flipped portalMenu>
          <OverflowMenuItem text="Go to Organization" on:click={goToOrganization(row.permission.group.organization.id)} />
          <OverflowMenuItem
            text="Go to Permission"
            on:click={goToPermission(row.permission.group.id, row.permission.id, row.permission.type)}
          />
        </OverflowMenu>
      {:else if cell.key === 'permission.type'}
        {getPermissionType(row)}
      {:else if cell.display}
        {cell.display(cell.value, row)}
      {:else}
        {cell.value}
      {/if}
    </svelte:fragment>
  </DataTable>
  {#if entitlements.length === 0 && !pageData.stale}
    <div class="empty-state">No entitlements found for the selected filters.</div>
  {/if}
  <PaginationWithRouting
    bind:page={pagination.currentPage}
    bind:pageSize={pagination.pageSize}
    pageSizes={[10, 25, 50]}
    totalItems={entitlementsCount}
    on:change={onPaginationChange}
  />
</LoadingSpinner>

<svelte:window on:popstate={onPopstateChange} />

<style>
  .filters-reset-button {
    display: flex;
    justify-content: flex-end;
  }

  @media (min-width: 42rem) {
    .filters-reset-button {
      margin-top: -2rem;
      right: 0px;
      position: absolute;
    }
  }

  .filters-row {
    display: flex;
    gap: 1em;
    flex-wrap: wrap;
  }

  .filters-row-margin {
    margin-top: 1em;
    margin-bottom: 2em;
  }

  .filters-row :global(.bx--multi-select__wrapper) {
    flex: 1 1 350px;
    min-width: calc(50% - 0.5em);
  }

  @media (min-width: 790px) {
    .filters-row :global(.bx--multi-select__wrapper) {
      max-width: calc(50% - 0.5em);
    }
  }

  .date-range-picker {
    display: flex;
    flex-wrap: wrap;
    gap: 1em;
  }

  .date-range-picker :global(.bx--date-picker) {
    align-items: start;
  }

  .generate-report-buttons {
    align-content: end;
    flex: auto;
    text-align: end;
  }

  .ongoing-cell {
    display: flex;
    align-items: center;
    gap: 0.5em;
  }

  .ongoing-cell em {
    font-size: 0.85em;
    color: var(--cds-text-helper);
  }

  .stale-data-notification {
    align-content: center;
    color: var(--cds-text-secondary);
    text-align: center;
  }

  .empty-state {
    align-content: center;
    background-color: var(--cds-layer);
    color: var(--cds-text-secondary);
    height: var(--cds-spacing-09);
    text-align: center;
  }
</style>
